// English dental vocabulary (FDI numbering, British and American spellings).
// Teeth can be named as digits ("16"), words ("sixteen", "twenty-six"),
// FDI pairs ("one six", "two-six"), UK shorthand ("UR6", "LL7") or by
// position with a side ("upper right six", "lower left first molar").

import { isValidTooth, type Surface } from '@/utils/dental';
import type {
  CodeRule,
  Lexicon,
  LocationWord,
  MaterialRule,
  NumberRead,
  PhraseHit,
  Token,
} from './types';

const UNITS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
};

const TEENS: Record<string, number> = {
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const TENS: [string, number][] = [
  ['seventy', 70],
  ['twenty', 20],
  ['thirty', 30],
  ['eighty', 80],
  ['forty', 40],
  ['fifty', 50],
  ['sixty', 60],
];

/** "UR6" → quadrant 1, position 6 (upper right / upper left / lower left / lower right). */
const QUADRANT_SHORT: Record<string, number> = { ur: 1, ul: 2, ll: 3, lr: 4 };

function read(value: number, used: number, adjectival = false): NumberRead {
  return { value, used, adjectival, from: false, to: false };
}

/** "twenty six", "twenty-six", "twentysix". */
function readTens(tokens: Token[], i: number, base: number, rest: string): NumberRead | null {
  if (rest) {
    const unit = UNITS[rest.replace(/^-/, '')];
    return unit ? read(base + unit, 1) : null;
  }
  const next = tokens[i + 1]?.norm;
  const unit = next ? UNITS[next] : undefined;
  return unit ? read(base + unit, 2) : null;
}

function readNumber(tokens: Token[], i: number): NumberRead | null {
  const norm = tokens[i].norm;
  const digits = /^(\d{2})(st|nd|rd|th)?$/.exec(norm);
  if (digits) return read(Number(digits[1]), 1, !!digits[2]);
  const short = /^(ur|ul|ll|lr)([1-8])$/.exec(norm);
  if (short) return read(QUADRANT_SHORT[short[1]] * 10 + Number(short[2]), 1, true);
  if (TEENS[norm]) return read(TEENS[norm], 1);
  const tens = TENS.find(([word]) => norm.startsWith(word));
  return tens ? readTens(tokens, i, tens[1], norm.slice(tens[0].length)) : null;
}

/** "one six" → 16, "four-seven" → 47. */
function readQuadrantPair(a: string, b: string): number | null {
  const q = UNITS[a];
  const p = UNITS[b];
  if (!(q && p) || q > 8 || p > 8) return null;
  const value = q * 10 + p;
  return isValidTooth(value) ? value : null;
}

/** Single words that name a position only after a side: "upper right six". */
const POSITION_NUMERALS: Record<string, number> = {
  ...UNITS,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
};

/** Tooth names, usable without a side ("the canine" still needs a quadrant: a question). */
const NAMED: [string[], number][] = [
  [['central', 'incisor'], 1],
  [['lateral', 'incisor'], 2],
  [['first', 'premolar'], 4],
  [['first', 'bicuspid'], 4],
  [['second', 'premolar'], 5],
  [['second', 'bicuspid'], 5],
  [['first', 'molar'], 6],
  [['second', 'molar'], 7],
  [['third', 'molar'], 8],
  [['wisdom', 'tooth'], 8],
  [['canine'], 3],
  [['cuspid'], 3],
  [['eye', 'tooth'], 3],
  [['wisdom'], 8],
];

function readPositionAt(tokens: Token[], i: number, afterLocation: boolean) {
  const n = tokens[i]?.norm;
  if (!n) return null;
  for (const [words, position] of NAMED) {
    if (words.every((w, k) => tokens[i + k]?.norm.startsWith(w))) {
      return { position, used: words.length };
    }
  }
  // A bare "six" is a count, not a tooth: only after "upper right" and the like.
  const numeral = POSITION_NUMERALS[n];
  return afterLocation && numeral && numeral <= 8 ? { position: numeral, used: 1 } : null;
}

const LOCATIONS: Record<string, LocationWord> = {
  upper: 'upper',
  maxillary: 'upper',
  top: 'upper',
  lower: 'lower',
  mandibular: 'lower',
  bottom: 'lower',
  right: 'right',
  left: 'left',
  primary: 'primary',
  deciduous: 'primary',
  baby: 'primary',
  milk: 'primary',
  the: 'filler',
  side: 'filler',
};

const CODE_RULES: CodeRule[] = [
  { code: 'root_canal', stems: ['rootcanal', 'endodont', 'endo', 'rct'] },
  { code: 'periapical', stems: ['periapical', 'apical', 'abscess', 'granuloma'] },
  { code: 'root_remnant', stems: ['radix'] },
  { code: 'devital', stems: ['devital', 'nonvital', 'non-vital', 'necrot'], exact: ['dead'] },
  { code: 'caries', stems: ['caries', 'carious', 'decay', 'cavit'] },
  { code: 'sealant', stems: ['sealant', 'sealed'] },
  { code: 'inlay', stems: ['inlay', 'onlay', 'overlay'] },
  { code: 'filling', stems: ['filling', 'filled', 'restor'] },
  { code: 'fracture', stems: ['fractur', 'crack', 'broken', 'chip'] },
  { code: 'bridge_pontic', stems: ['pontic'] },
  { code: 'bridge_abutment', stems: ['abutment'] },
  { code: 'bridge_abutment', exact: ['bridge', 'bridges', 'bridged'], genericBridge: true },
  { code: 'crown', stems: ['crown'] },
  { code: 'veneer', stems: ['veneer'] },
  { code: 'extraction', stems: ['extract', 'xla'], exact: ['pull', 'pulled'] },
  {
    code: 'missing',
    // "avulsed", "exfoliated" (a primary tooth shed), "edentulous"
    stems: ['missing', 'absent', 'avuls', 'exfoliat', 'edentul'],
    // Not "gone": "pain gone" is not a missing tooth.
    exact: ['lost'],
  },
  { code: 'implant', stems: ['implant'] },
  { code: 'mobility', stems: ['mobil', 'loose', 'wobbl'] },
  { code: 'calculus', stems: ['calculus', 'tartar'] },
  { code: 'impacted', stems: ['impact', 'unerupt'] },
  { code: 'wear', stems: ['wear', 'worn', 'attrition', 'erosion', 'abrasion', 'abfraction'] },
  { code: 'sound', exact: ['sound', 'healthy', 'intact', 'nad'] },
];

const SURFACE_STEMS: [string, Surface][] = [
  ['mesi', 'M'],
  ['dist', 'D'],
  ['occlus', 'O'],
  // "incisal", not "incisor"
  ['incisa', 'I'],
  ['bucc', 'B'],
  ['labi', 'B'],
  ['facia', 'B'],
  ['vestib', 'B'],
  ['lingu', 'L'],
  ['palat', 'L'],
];

const MATERIAL_RULES: MaterialRule[] = [
  { material: 'metal_ceramic', stems: ['pfm', 'metalceramic', 'metal-ceramic'] },
  { material: 'lithium_disilicate', stems: ['emax', 'e-max', 'lithium', 'disilicate'] },
  { material: 'zirconia', stems: ['zircon'] },
  { material: 'glass_ionomer', stems: ['glassionomer', 'ionomer', 'gic'] },
  { material: 'composite', stems: ['composite', 'resin'] },
  { material: 'amalgam', stems: ['amalgam'] },
  { material: 'ceramic', stems: ['ceramic', 'porcelain'] },
  { material: 'gold', stems: ['gold'] },
  { material: 'titanium', stems: ['titanium'] },
  { material: 'temporary', stems: ['temporar', 'provisional'] },
];

/** Two-word phrases: [first word, second word, what they mean]. */
const PHRASES: [RegExp, RegExp, PhraseHit][] = [
  // "root remnant", "root stump"
  [/^root$/, /^(remnant|stump|fragment)s?$/, { kind: 'code', code: 'root_remnant', used: 2 }],
  [/^non$/, /^vital/, { kind: 'code', code: 'devital', used: 2 }],
  // "root canal", "root canal treated", "root filled", "root filling"
  [/^root$/, /^(canal|fill)/, { kind: 'code', code: 'root_canal', used: 2 }],
  // "fell out", "fallen out", "knocked out", "came out"
  [/^(fell|fallen|falls|knocked|came|come)$/, /^out$/, { kind: 'code', code: 'missing', used: 2 }],
  [/^metal$/, /^ceramic/, { kind: 'material', material: 'metal_ceramic', used: 2 }],
  [/^glass$/, /^ionomer/, { kind: 'material', material: 'glass_ionomer', used: 2 }],
  // "white filling" = composite, "silver filling" = amalgam (the filling is read next).
  [/^white$/, /^fill/, { kind: 'material', material: 'composite', used: 1 }],
  [/^silver$/, /^fill/, { kind: 'material', material: 'amalgam', used: 1 }],
];

function readPhrase(tokens: Token[], i: number): PhraseHit | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm ?? '';
  // "retained root", but not "retained root canal filling"
  if (n === 'retained' && /^roots?$/.test(next)) {
    const third = tokens[i + 2]?.norm ?? '';
    return /^(canal|fill)/.test(third) ? null : { kind: 'code', code: 'root_remnant', used: 2 };
  }
  return PHRASES.find(([a, b]) => a.test(n) && b.test(next))?.[2] ?? null;
}

const NOTES: [string, string][] = [
  ['secondary', 'secondary'],
  ['recurrent', 'recurrent'],
  ['deep', 'deep'],
  ['incipient', 'incipient'],
  ['early', 'early'],
  ['defective', 'defective'],
  ['leaking', 'leaking'],
  ['failing', 'failing'],
  ['old', 'old'],
];

const GRADES: Record<string, string> = {
  one: 'I',
  '1': 'I',
  i: 'I',
  first: 'I',
  two: 'II',
  '2': 'II',
  ii: 'II',
  second: 'II',
  three: 'III',
  '3': 'III',
  iii: 'III',
  third: 'III',
};

/** "grade two", "grade II", "second degree". */
function readGrade(tokens: Token[], i: number): { grade: string; used: number } | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm ?? '';
  if ((n === 'grade' || n === 'class' || n === 'degree') && GRADES[next]) {
    return { grade: GRADES[next], used: 2 };
  }
  if (GRADES[n] && (next.startsWith('degree') || next === 'grade')) {
    return { grade: GRADES[n], used: 2 };
  }
  return null;
}

const FILLER_WORDS = new Set([
  'tooth',
  'teeth',
  'number',
  'a',
  'an',
  'the',
  'on',
  'of',
  'is',
  'are',
  'has',
  'have',
  'with',
  'and',
  'there',
  'surface',
  'surfaces',
  'side',
  'at',
  'in',
  'area',
  'region',
  'also',
  'existing',
  'present',
  'noted',
  'seen',
  'visible',
  'please',
  'its',
  'it',
  'treated',
  'treatment',
]);

export const en: Lexicon = {
  lang: 'en',
  readNumber,
  readQuadrantPair,
  readPosition: (n) => (n === 'canine' || n === 'cuspid' ? 3 : n.startsWith('wisdom') ? 8 : null),
  readPositionAt,
  location: (n) => LOCATIONS[n] ?? null,
  rangeFrom: new Set(['from']),
  rangeTo: new Set(['to', 'through', 'until']),
  readPhrase,
  codeRules: CODE_RULES,
  surfaceStems: SURFACE_STEMS,
  // "do" and "od" are English words; "mo" and "mod" are safe.
  lowercaseSurfaceAbbr: new Set(['mod', 'mo', 'modb', 'modl']),
  // "I" at the start of a sentence is the pronoun, not "incisal".
  excludedSurfaceAbbr: new Set(['I']),
  materialRules: MATERIAL_RULES,
  isPlanned: (n) =>
    ['plan', 'needs', 'need', 'indicated', 'required', 'scheduled', 'todo'].some((st) =>
      n.startsWith(st)
    ),
  note: (n) => NOTES.find(([stem]) => n.startsWith(stem))?.[1] ?? null,
  negationWords: new Set(['no', 'not', 'without', 'none', 'nil']),
  correctionWords: new Set(['but', 'rather']),
  fillerWords: FILLER_WORDS,
  separatorWords: new Set(['then', 'next', 'afterwards']),
  commandPatterns: [
    {
      command: 'approve_all',
      pattern: /^(approve( them)?( all| everything)?|approve all of them|accept all)$/,
    },
    {
      command: 'undo',
      pattern: /^(undo|undo that|cancel that|scratch that|delete (the )?last( one)?)$/,
    },
    { command: 'yes', pattern: /^(yes|yeah|yep)$/ },
    { command: 'no', pattern: /^(no|nope)$/ },
  ],
  noChangePattern:
    /(no change|unchanged|everything else (is )?(fine|ok|the same|unchanged)|rest (is )?(fine|unchanged))/,
  price: { unit: 'dinars?|rsd', thousand: 'thousand' },
  readGrade,
};
